import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { parseComplexDate } from "shared/util/util";
import Grid from "@mui/material/Grid";
import CustomSelect from "shared/components/FormElements/Select";
import Button from "shared/components/FormElements/Button";
import CheckBox from "shared/components/FormElements/CheckBox";
import { useForm } from "shared/hooks/form-hook";
import DatePicker from "shared/components/FormElements/DatePicker";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import ExcelJS from "exceljs";
import { TRAVEL_STATUS_TYPE_EXPLANATIONS } from "shared/util/types";
import { SERVICE_TYPES, ROUTE_TYPES } from "@routebot/constants";
import { SERVICE_COMPLETION_STATUS_TYPES_TR, SERVICE_COMPLETION_STATUS_TYPES_EN } from "shared/utility/types";
import { TRANSFER_REPORT_SERVICE_TYPES_KEY } from "shared/utility/constants";
import { getCurrentLanguage } from "shared/utility/lang";
const { isActiveOptions } = require("shared/util/types");
import { usePersonelServiceBackend } from "shared/hooks/backend/personel-service-hook";
import ManagedTransfer from "features/transfers/managed-transfer/ManagedTransfer";
import Modal from "shared/components/UIElements/Modal";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const TransferReport = () => {
  const { t } = useTranslation();
  const store = useContext(StoreContext);
  const auth = useContext(AuthContext);
  const { isLoading, sendRequest } = useHttpClient();
  const { updatePersonelService, createUetds } = usePersonelServiceBackend();
  const [today, setToday] = useState(null);
  const [calculatedTransfers, setCalculatedTransfers] = useState([]);
  const [initDateSelected, setInitDateSelected] = useState();
  const [finishDateSelected, setFinishDateSelected] = useState();

  const [storeVehicleRoutes, setStoreVehicleRoutes] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(() => {
    try {
      const stored = localStorage.getItem(TRANSFER_REPORT_SERVICE_TYPES_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return { personel: true, shift: true, transfer: true };
  });

  useEffect(() => {
    localStorage.setItem(TRANSFER_REPORT_SERVICE_TYPES_KEY, JSON.stringify(selectedServiceTypes));
  }, [selectedServiceTypes]);

  const { filteredSchoolOptions, schoolOptionIds } = useMemo(() => {
    if (!Array.isArray(store.schoolOptions)) {
      return { filteredSchoolOptions: [], schoolOptionIds: [] };
    }
    const options = store.schoolOptions.filter((option) => option?.value !== undefined && option?.value !== null);
    return {
      filteredSchoolOptions: options,
      schoolOptionIds: options.map((option) => option.value.toString()),
    };
  }, [store.schoolOptions]);

  const areAllSchoolsSelected = schoolOptionIds.length > 0 && selectedSchools.length === schoolOptionIds.length;

  const [itemState] = useState(Date.now());

  const [savingVehicleRouteIds, setSavingVehicleRouteIds] = useState(new Set());
  const [rowStatuses, setRowStatuses] = useState(new Map());
  const [recentlyPassiveIds, setRecentlyPassiveIds] = useState(new Set());

  const rowStatusTimers = useRef(new Map());

  const setRowStatus = useCallback((vehicleRouteId, status) => {
    setRowStatuses((prev) => {
      const next = new Map(prev);
      if (status === null) {
        next.delete(vehicleRouteId);
      } else {
        next.set(vehicleRouteId, status);
      }
      return next;
    });
    if (status === "saved") {
      const existingTimer = rowStatusTimers.current.get(vehicleRouteId);
      if (existingTimer) clearTimeout(existingTimer);
      const timer = setTimeout(() => {
        setRowStatuses((prev) => {
          const next = new Map(prev);
          if (next.get(vehicleRouteId) === "saved") {
            next.delete(vehicleRouteId);
          }
          return next;
        });
        rowStatusTimers.current.delete(vehicleRouteId);
      }, 2000);
      rowStatusTimers.current.set(vehicleRouteId, timer);
    }
  }, []);

  const [defaultFormat] = useState(true);

  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState(null);

  const serviceCompletionStatusOptions =
    getCurrentLanguage() === "tr" ? SERVICE_COMPLETION_STATUS_TYPES_TR : SERVICE_COMPLETION_STATUS_TYPES_EN;

  const getCompletionStatusLabel = (statusValue) => {
    if (statusValue === null || statusValue === undefined) {
      return "-";
    }

    const statusOption = serviceCompletionStatusOptions.find(
      (option) => option.value?.toString() === statusValue?.toString(),
    );

    return statusOption?.label || "-";
  };

  const handleServiceModalClose = () => {
    createReport();
    setShowServiceModal(false);
  };

  const [formState, inputHandler] = useForm(
    {
      datestarter: { value: "", isValid: false },
      datefinisher: { value: "", isValid: false },
      showAll: { value: false, isValid: true },
    },
    false,
  );

  const [formStateDriver, inputHandlerDriver] = useForm(
    {
      driver: { value: "", isValid: true },
      plate: { value: "", isValid: true },
    },
    true,
  );

  useEffect(() => {
    if (!schoolOptionIds.length) {
      setSelectedSchools((prevSelected) => (prevSelected.length ? [] : prevSelected));
      return;
    }
    setSelectedSchools((prevSelected) => {
      if (!prevSelected.length) {
        return [...schoolOptionIds];
      }
      const filteredSelection = prevSelected.filter((id) => schoolOptionIds.includes(id));
      if (filteredSelection.length !== prevSelected.length) {
        return filteredSelection;
      }
      return prevSelected;
    });
  }, [schoolOptionIds]);

  const handleSelectAllSchools = (checked) => {
    if (checked) {
      setSelectedSchools([...schoolOptionIds]);
      return;
    }
    setSelectedSchools([]);
  };

  const handleSchoolToggle = (schoolId) => {
    if (!schoolId) {
      return;
    }
    setSelectedSchools((prevSelected) => {
      if (prevSelected.includes(schoolId)) {
        return prevSelected.filter((id) => id !== schoolId);
      }
      return [...prevSelected, schoolId];
    });
  };

  const LEGACY_PICKUP_TYPE = "100";
  const LEGACY_DROPOFF_TYPE = "200";

  const isWeekday = (date) => {
    const weekday = date.getDay();
    return weekday >= 1 && weekday <= 5;
  };

  const cloneAndNormalizeDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return null;
    }
    const clonedDate = new Date(date);
    clonedDate.setHours(0, 0, 0, 0);
    return clonedDate;
  };

  const formatDateToISO = (date) => {
    return date.toISOString().split("T")[0];
  };

  const normalizeToISODateString = (value) => {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return formatDateToISO(value);
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return formatDateToISO(parsed);
    }
    if (typeof value === "string") {
      return value.split("T")[0] || null;
    }
    return null;
  };

  const normalizeSchoolId = (schoolValue) => {
    if (!schoolValue) {
      return null;
    }
    if (typeof schoolValue === "object") {
      const possibleIdentifiers = [schoolValue.id, schoolValue._id, schoolValue.value];
      for (let i = 0; i < possibleIdentifiers.length; i++) {
        const currentIdentifier = possibleIdentifiers[i];
        if (currentIdentifier !== undefined && currentIdentifier !== null) {
          return currentIdentifier.toString();
        }
      }
      if (schoolValue.toString) {
        return schoolValue.toString();
      }
      return null;
    }
    return schoolValue.toString();
  };

  const isSchoolWithinSelection = (schoolValue, isAllSelected, selectedSchoolIdsSet) => {
    if (isAllSelected || !selectedSchoolIdsSet?.size) {
      return true;
    }
    const normalizedSchoolId = normalizeSchoolId(schoolValue);
    if (!normalizedSchoolId) {
      return false;
    }
    return selectedSchoolIdsSet.has(normalizedSchoolId);
  };

  const getRouteIdentifier = (route) => {
    if (!route) {
      return null;
    }

    const possibleIdentifiers = [route.originalRouteId, route.id, route._id];

    for (let i = 0; i < possibleIdentifiers.length; i++) {
      const currentIdentifier = possibleIdentifiers[i];
      if (!currentIdentifier) {
        continue;
      }
      if (typeof currentIdentifier === "object" && currentIdentifier.toString) {
        return currentIdentifier.toString();
      }
      return currentIdentifier.toString();
    }

    return null;
  };

  const buildPersonelServiceKey = (routeIdentifier, dateString) => {
    if (!routeIdentifier || !dateString) {
      return null;
    }
    return `${routeIdentifier}-${dateString}`;
  };

  const buildShiftServiceKey = (serviceId, dateString) => {
    if (!serviceId || !dateString) {
      return null;
    }
    return `${serviceId}-${dateString}`;
  };

  const parseShiftServiceDate = (dateValue) => {
    if (!dateValue) {
      return null;
    }
    if (dateValue instanceof Date) {
      return cloneAndNormalizeDate(dateValue);
    }
    return cloneAndNormalizeDate(new Date(dateValue));
  };

  const isSyntheticRoutePayload = (route) => {
    return Boolean(route?.isSyntheticPersonelService || route?.isSyntheticShiftService);
  };

  const isSyntheticTransferRow = (transfer) => {
    if (!transfer) {
      return false;
    }
    return Boolean(
      transfer.isSyntheticPersonelService ||
      transfer.isSyntheticShiftService ||
      isSyntheticRoutePayload(transfer.route),
    );
  };

  const buildTransferTimeString = (timeString) => {
    const safeTime = timeString && timeString.includes(":") ? timeString : "00:00";
    const normalizedTime = safeTime.length === 5 ? `${safeTime}:00` : safeTime;
    const mockDateString = `1970-01-01T${normalizedTime}`;
    const parsedDate = new Date(mockDateString);
    if (Number.isNaN(parsedDate.getTime())) {
      return `Thu Jan 01 1970 ${normalizedTime}`;
    }
    return parsedDate.toString();
  };

  const deriveLegacyRouteType = (routeType, legacyTypeFallback) => {
    if (legacyTypeFallback !== undefined && legacyTypeFallback !== null) {
      return legacyTypeFallback.toString();
    }
    if (routeType && routeType.toString() === ROUTE_TYPES.DROP_OFF) {
      return LEGACY_DROPOFF_TYPE;
    }
    return LEGACY_PICKUP_TYPE;
  };

  const getVehicleRoutePassengers = (vehicleRoute) => {
    if (
      !vehicleRoute ||
      !vehicleRoute.routelocations ||
      !Array.isArray(vehicleRoute.routelocations) ||
      vehicleRoute.routelocations.length <= 1
    ) {
      return [];
    }

    const passengers = [];
    for (let k = 1; k < vehicleRoute.routelocations.length; k++) {
      try {
        const currentPersonId = vehicleRoute.routelocations[k].student;
        const currentPersonTransferTime = vehicleRoute.routelocations[k].transfertime;
        const currentStudent = store.students.find((item) => item._id.toString() === currentPersonId.toString());
        if (!currentStudent) {
          continue;
        }
        const currentPersonName = currentStudent.name;
        passengers.push(
          currentPersonTransferTime ? `${currentPersonTransferTime} - ${currentPersonName}` : currentPersonName,
        );
      } catch {}
    }
    return passengers;
  };

  const getPersonelServicePassengers = (personelService) => {
    if (!personelService || !Array.isArray(personelService.users)) {
      return [];
    }

    const passengers = [];
    for (let k = 0; k < personelService.users.length; k++) {
      try {
        const currentUser = personelService.users[k];
        const currentPersonTransferTime = personelService.usersdetails?.find(
          (userDetail) => userDetail.user.toString() === currentUser.id?.toString(),
        )?.transfertime;
        const currentPersonName = currentUser.name;
        const currentPersonId = currentUser.id;
        const userRecord = store.users.find((item) => item._id.toString() === currentPersonId.toString());
        const userClass = userRecord?.cardId || null;
        const userRegionId = userRecord?.transferregion;
        const normalizedUserRegionId =
          typeof userRegionId === "object" && userRegionId !== null && userRegionId.toString
            ? userRegionId.toString()
            : userRegionId;
        const userRegion =
          store.transferregions.find((item) => item._id.toString() === normalizedUserRegionId?.toString()) || null;

        let textToAdd = currentPersonTransferTime
          ? `${currentPersonTransferTime} - ${currentPersonName}`
          : currentPersonName;
        if (userClass) {
          textToAdd += ` (${userClass})`;
        }
        if (userRegion) {
          textToAdd += ` (${userRegion.name})`;
        }

        passengers.push(textToAdd);
      } catch {}
    }
    return passengers;
  };

  const getPassengersForTransfer = (vehicleRoute, personelService) => {
    const vehicleRoutePassengers = getVehicleRoutePassengers(vehicleRoute);
    const personelPassengers = getPersonelServicePassengers(personelService);
    return [...vehicleRoutePassengers, ...personelPassengers].sort();
  };

  const getWeekdayDatesInRange = (startDate, endDate) => {
    const normalizedStart = cloneAndNormalizeDate(startDate);
    const normalizedEnd = cloneAndNormalizeDate(endDate);

    if (!normalizedStart || !normalizedEnd || normalizedStart > normalizedEnd) {
      return [];
    }

    const weekdays = [];
    const cursor = new Date(normalizedStart);
    while (cursor <= normalizedEnd) {
      if (isWeekday(cursor)) {
        weekdays.push(new Date(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return weekdays;
  };

  const getEligiblePersonelServices = (isAllSelected, selectedSchoolIdsSet) => {
    return store.standartPersonelServices.filter((service) => {
      if (service.serviceType !== SERVICE_TYPES.PERSONEL) {
        return false;
      }
      if (service.isActive === false) {
        return false;
      }
      return isSchoolWithinSelection(service.school, isAllSelected, selectedSchoolIdsSet);
    });
  };

  const buildSyntheticPersonelTransfers = (weekdayDates, personelServices, excludedKeys = new Set()) => {
    const syntheticTransfers = [];
    weekdayDates.forEach((date) => {
      const dateString = formatDateToISO(date);
      personelServices.forEach((service) => {
        const serviceIdentifier = getRouteIdentifier(service);
        const uniqueServiceKey = buildPersonelServiceKey(serviceIdentifier, dateString);
        if (!uniqueServiceKey || excludedKeys.has(uniqueServiceKey)) {
          return;
        }
        excludedKeys.add(uniqueServiceKey);
        const legacyType = deriveLegacyRouteType(service.routeType, service.type);
        const transferTime = buildTransferTimeString(service.routeClientArrivalTime || service.starttime || "00:00");
        const passengers = getPassengersForTransfer(null, service);
        const syntheticTransferId = `synthetic-${service.id}-${dateString}`;
        const originalRouteId = service._id || service.id || `personel-${service.name}`;
        syntheticTransfers.push({
          transferInfo: {
            _id: syntheticTransferId,
            id: syntheticTransferId,
            name: service.name,
            transferdate: dateString,
            transfertime: transferTime,
            type: legacyType,
            school: service.school?.id || service.school?._id || service.school,
          },
          route: {
            ...service,
            _id: `${originalRouteId}-synthetic-${dateString}`,
            originalRouteId,
            isSyntheticPersonelService: true,
            type: legacyType,
          },
          people: passengers,
          isSyntheticPersonelService: true,
          uniqueKey: `${syntheticTransferId}-${originalRouteId}`,
        });
      });
    });
    return syntheticTransfers;
  };

  const getSyntheticTransfersForRange = (
    startDate,
    endDate,
    isAllSelected,
    selectedSchoolIdsSet,
    excludedKeys = new Set(),
  ) => {
    const weekdayDates = getWeekdayDatesInRange(startDate, endDate);
    if (!weekdayDates.length) {
      return [];
    }
    const eligibleServices = getEligiblePersonelServices(isAllSelected, selectedSchoolIdsSet);
    if (!eligibleServices.length) {
      return [];
    }
    return buildSyntheticPersonelTransfers(weekdayDates, eligibleServices, excludedKeys);
  };

  const getEligibleShiftServices = (isAllSelected, selectedSchoolIdsSet, startDate, endDate) => {
    if (!Array.isArray(store.shiftPersonelServices) || !store.shiftPersonelServices.length) {
      return [];
    }

    const normalizedStart = cloneAndNormalizeDate(startDate);
    const normalizedEnd = cloneAndNormalizeDate(endDate);
    if (!normalizedStart || !normalizedEnd) {
      return [];
    }

    return store.shiftPersonelServices.filter((service) => {
      if (service.serviceType !== SERVICE_TYPES.SHIFT) {
        return false;
      }
      if (!isSchoolWithinSelection(service.school, isAllSelected, selectedSchoolIdsSet)) {
        return false;
      }
      const serviceDate = parseShiftServiceDate(service.date);
      if (!serviceDate) {
        return false;
      }
      return serviceDate >= normalizedStart && serviceDate <= normalizedEnd;
    });
  };

  const buildShiftTransfers = (shiftServices, existingKeys = new Set()) => {
    if (!shiftServices.length) {
      return [];
    }

    const shiftTransfers = [];

    shiftServices.forEach((service) => {
      const serviceDateString = normalizeToISODateString(service.date);
      const uniqueKey = buildShiftServiceKey(service.id || service._id, serviceDateString);
      if (!uniqueKey || existingKeys.has(uniqueKey)) {
        return;
      }
      existingKeys.add(uniqueKey);

      const legacyType = deriveLegacyRouteType(service.routeType, service.type);
      const transferTime = buildTransferTimeString(service.routeClientArrivalTime || service.starttime || "00:00");
      const passengers = getPassengersForTransfer(null, service);
      const syntheticTransferId = `shift-${service.id || service._id}-${serviceDateString}`;
      const originalRouteId = service._id || service.id || `shift-${service.name}`;

      shiftTransfers.push({
        transferInfo: {
          _id: syntheticTransferId,
          id: syntheticTransferId,
          name: service.name,
          transferdate: serviceDateString,
          transfertime: transferTime,
          type: legacyType,
          school: service.school?.id || service.school?._id || service.school,
        },
        route: {
          ...service,
          _id: `${originalRouteId}-shift-${serviceDateString}`,
          originalRouteId,
          isSyntheticShiftService: true,
          type: legacyType,
        },
        people: passengers,
        isSyntheticShiftService: true,
        uniqueKey: `${syntheticTransferId}-${originalRouteId}`,
      });
    });

    return shiftTransfers;
  };

  const matchesRouteIdentifier = (route, routeId) => {
    if (!route || !routeId) {
      return false;
    }
    const normalizedId = routeId.toString();
    return (
      route?._id?.toString() === normalizedId ||
      route?.id?.toString() === normalizedId ||
      route?.originalRouteId?.toString() === normalizedId
    );
  };

  useEffect(() => {
    const todayString = new Date();
    const date = parseComplexDate(todayString);
    setToday(date);
    setInitDateSelected(date);
    setFinishDateSelected(date);

    setStoreVehicleRoutes(store.vehicleRoutes);
  }, [setToday, setInitDateSelected, setFinishDateSelected, store.vehicleRoutes]);

  const createReport = async () => {
    let allTransfers = [];

    if (!selectedSchools.length || !schoolOptionIds.length) {
      window.toast.error(t("transferReport.selectSchoolError"));
      return;
    }

    const normalizedSelectedSchoolIds = selectedSchools.map((schoolId) => schoolId?.toString()).filter(Boolean);
    if (!normalizedSelectedSchoolIds.length) {
      window.toast.error(t("transferReport.selectSchoolError"));
      return;
    }

    const selectedSchoolIdsSet = new Set(normalizedSelectedSchoolIds);
    const isAllSelected = selectedSchoolIdsSet.size === schoolOptionIds.length;

    const startDate = new Date(formState.inputs.datestarter.value);
    const endDate = new Date(formState.inputs.datefinisher.value);

    setInitDateSelected(parseComplexDate(startDate));
    setFinishDateSelected(parseComplexDate(endDate));

    allTransfers = store.transfers.filter(
      (transfer) =>
        new Date(transfer.transferdate) >= startDate &&
        new Date(transfer.transferdate) <= endDate &&
        isSchoolWithinSelection(transfer.school, isAllSelected, selectedSchoolIdsSet),
    );

    let calculatedTransfers = [];

    for (let m = 0; m < allTransfers.length; m++) {
      const currentTransfer = allTransfers[m];

      for (let i = 0; i < currentTransfer.vehicleRoutes.length; i++) {
        const currentVehicleRoute = storeVehicleRoutes.find(
          (vehicleRoute) => vehicleRoute?._id.toString() === currentTransfer.vehicleRoutes[i].toString(),
        );

        const currentPersonelService = store.standartPersonelServices.find(
          (personelService) => personelService.id === currentTransfer.vehicleRoutes[i].toString(),
        );

        const currentPeople = getPassengersForTransfer(currentVehicleRoute, currentPersonelService);
        const routePayload = currentVehicleRoute || currentPersonelService;

        if (routePayload) {
          const uniqueKey = `${currentTransfer?._id || currentTransfer?.id || "transfer"}-${
            routePayload?._id || routePayload?.id || i
          }`;
          calculatedTransfers.push({
            transferInfo: currentTransfer,
            route: routePayload,
            people: currentPeople,
            uniqueKey,
          });
        }
      }
    }

    const existingPersonelServiceKeys = new Set();
    calculatedTransfers.forEach((transfer) => {
      if (
        transfer.route?.serviceType !== SERVICE_TYPES.PERSONEL ||
        isSyntheticRoutePayload(transfer.route) ||
        !transfer.transferInfo
      ) {
        return;
      }
      const routeIdentifier = getRouteIdentifier(transfer.route);
      const transferDate = normalizeToISODateString(transfer.transferInfo.transferdate);
      const serviceKey = buildPersonelServiceKey(routeIdentifier, transferDate);
      if (serviceKey) {
        existingPersonelServiceKeys.add(serviceKey);
      }
    });

    if (selectedServiceTypes.personel) {
      const syntheticTransfers = getSyntheticTransfersForRange(
        startDate,
        endDate,
        isAllSelected,
        selectedSchoolIdsSet,
        existingPersonelServiceKeys,
      );
      if (syntheticTransfers.length) {
        calculatedTransfers = [...calculatedTransfers, ...syntheticTransfers];
      }
    }

    if (selectedServiceTypes.shift) {
      const existingShiftServiceKeys = new Set();
      calculatedTransfers.forEach((transfer) => {
        if (
          transfer.route?.serviceType !== SERVICE_TYPES.SHIFT ||
          isSyntheticRoutePayload(transfer.route) ||
          !transfer.transferInfo
        ) {
          return;
        }
        const routeIdentifier = getRouteIdentifier(transfer.route);
        const transferDate = normalizeToISODateString(transfer.transferInfo.transferdate);
        const serviceKey = buildShiftServiceKey(routeIdentifier, transferDate);
        if (serviceKey) {
          existingShiftServiceKeys.add(serviceKey);
        }
      });

      const eligibleShiftServices = getEligibleShiftServices(isAllSelected, selectedSchoolIdsSet, startDate, endDate);
      const shiftTransfers = buildShiftTransfers(eligibleShiftServices, existingShiftServiceKeys);
      if (shiftTransfers.length) {
        calculatedTransfers = [...calculatedTransfers, ...shiftTransfers];
      }
    }

    calculatedTransfers = calculatedTransfers.filter((transfer) => {
      const st = transfer.route?.serviceType;
      if (st === SERVICE_TYPES.PERSONEL) return selectedServiceTypes.personel;
      if (st === SERVICE_TYPES.SHIFT) return selectedServiceTypes.shift;
      return selectedServiceTypes.transfer;
    });

    let sortedTransfers = calculatedTransfers.sort((t1, t2) =>
      t1.transferInfo.transferdate + t1.transferInfo.transfertime.split(" ")[4].substring(0, 5) >
      t2.transferInfo.transferdate + t2.transferInfo.transfertime.split(" ")[4].substring(0, 5)
        ? 1
        : -1,
    );

    setCalculatedTransfers(sortedTransfers);

    if (sortedTransfers.length === 0) {
      window.toast.error(t("transferReport.noTransfersInRange"));
    }
  };

  const peopleComponent = (people) => {
    if (!people || people.length === 0) {
      return <p>{t("transferReport.noPassengers")}</p>;
    }

    return (
      <div>
        {people.map((person) => (
          <p key={person + Math.random()}> {person}</p>
        ))}
      </div>
    );
  };

  const saveDriverHandler = async (vehicleRouterId, selectedDriverId) => {
    setSavingVehicleRouteIds((prev) => new Set(prev).add(vehicleRouterId));
    setRowStatus(vehicleRouterId, "saving");

    const currentDriver = store.drivers.find((driver) => driver.id === selectedDriverId);
    const currentPlate = currentDriver?.plate || "-";

    const personelService = await updatePersonelService(vehicleRouterId, {
      drivers: [selectedDriverId],
      plate: currentPlate,
    });

    if (!personelService) {
      setRowStatus(vehicleRouterId, "error");
      setSavingVehicleRouteIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleRouterId);
        return next;
      });
      return;
    }

    store.UpdatePersonelService(personelService);

    const driverOption = store.driverOptions.find((driver) => driver.value === selectedDriverId);

    setCalculatedTransfers((prev) =>
      prev.map((transfer) => {
        if (isSyntheticRoutePayload(transfer.route) || !matchesRouteIdentifier(transfer.route, vehicleRouterId)) {
          return transfer;
        }
        const updatedDriver = {
          ...driverOption,
          _id: selectedDriverId,
          id: selectedDriverId,
          name: driverOption?.label,
        };
        return {
          ...transfer,
          route: {
            ...transfer.route,
            drivers: [updatedDriver, ...transfer.route.drivers.slice(1)],
            plate: currentPlate,
            uetdsSeferReferansNo: personelService?.uetdsSeferReferansNo,
          },
        };
      }),
    );

    setRowStatus(vehicleRouterId, "saved");

    setSavingVehicleRouteIds((prev) => {
      const next = new Set(prev);
      next.delete(vehicleRouterId);
      return next;
    });
  };

  const saveIsActiveHandler = async (vehicleRouterId, isActive) => {
    setSavingVehicleRouteIds((prev) => new Set(prev).add(vehicleRouterId));
    setRowStatus(vehicleRouterId, "saving");

    if (!isActive) {
      setRecentlyPassiveIds((prev) => new Set(prev).add(vehicleRouterId));
    }

    const cleanupAfterSave = (status) => {
      setRowStatus(vehicleRouterId, status);
      setSavingVehicleRouteIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleRouterId);
        return next;
      });
      if (!isActive) {
        setTimeout(() => {
          setRecentlyPassiveIds((prev) => {
            const next = new Set(prev);
            next.delete(vehicleRouterId);
            return next;
          });
        }, 2000);
      }
    };

    const personelService = await updatePersonelService(vehicleRouterId, { isActive });

    if (!personelService) {
      cleanupAfterSave("error");
      return;
    }

    store.UpdatePersonelService(personelService);

    setCalculatedTransfers((prev) =>
      prev.map((transfer) => {
        if (isSyntheticRoutePayload(transfer.route) || !matchesRouteIdentifier(transfer.route, vehicleRouterId)) {
          return transfer;
        }
        return {
          ...transfer,
          route: { ...transfer.route, isActive },
        };
      }),
    );

    const parentTransfer = store.transfers.find((transfer) =>
      transfer.vehicleRoutes?.some((vrId) => vrId?.toString() === vehicleRouterId?.toString()),
    );

    if (parentTransfer) {
      let allPassive = true;
      for (const vrId of parentTransfer.vehicleRoutes) {
        const vrIdStr = vrId?.toString();
        if (vrIdStr === vehicleRouterId?.toString()) {
          if (isActive) {
            allPassive = false;
            break;
          }
        } else {
          const ps = store.standartPersonelServices.find((s) => s.id === vrIdStr || s._id?.toString() === vrIdStr);
          const vr = storeVehicleRoutes.find((v) => v._id?.toString() === vrIdStr);
          const route = ps || vr;
          if (!route || route.isActive !== false) {
            allPassive = false;
            break;
          }
        }
      }

      try {
        const transferResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/transfers/${parentTransfer._id}`,
          "PATCH",
          JSON.stringify({ done: allPassive }),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          },
        );
        if (transferResponse?.transfer) {
          store.UpdateTransfer(transferResponse.transfer);
        }
      } catch (err) {
        cleanupAfterSave("error");
        return;
      }
    }

    cleanupAfterSave("saved");
  };

  const saveCompletionStatusHandler = async (vehicleRouterId, completionStatus) => {
    setSavingVehicleRouteIds((prev) => new Set(prev).add(vehicleRouterId));
    setRowStatus(vehicleRouterId, "saving");

    const personelService = await updatePersonelService(vehicleRouterId, { completionStatus });

    if (!personelService) {
      setRowStatus(vehicleRouterId, "error");
      setSavingVehicleRouteIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleRouterId);
        return next;
      });
      return;
    }

    store.UpdatePersonelService(personelService);

    setCalculatedTransfers((prev) =>
      prev.map((transfer) => {
        if (isSyntheticRoutePayload(transfer.route) || !matchesRouteIdentifier(transfer.route, vehicleRouterId)) {
          return transfer;
        }
        return {
          ...transfer,
          route: { ...transfer.route, completionStatus },
        };
      }),
    );

    setRowStatus(vehicleRouterId, "saved");

    setSavingVehicleRouteIds((prev) => {
      const next = new Set(prev);
      next.delete(vehicleRouterId);
      return next;
    });
  };

  const getPlateMiddleLetters = (plate) => {
    if (!plate) return "";
    const normalized = plate.toString().toLocaleUpperCase("tr-TR").replace(/\s+/g, "");
    const match = normalized.match(/^\d{2}([A-ZÇĞİÖŞÜ]+)\d+$/);
    return match ? match[1] : "";
  };

  const isInvalidPlate = (plate) => {
    if (!plate) return false;
    return plate.toString().replace(/\s+/g, "") === "-";
  };

  const shouldShowCreateUetds = (vehicleRoute) => {
    if (!vehicleRoute || isSyntheticRoutePayload(vehicleRoute) || vehicleRoute.uetdsSeferReferansNo) {
      return false;
    }
    if (isInvalidPlate(vehicleRoute.plate)) {
      return false;
    }
    return getPlateMiddleLetters(vehicleRoute.plate) !== "C";
  };

  const createUetdsHandler = async (vehicleRouterId) => {
    setSavingVehicleRouteIds((prev) => new Set(prev).add(vehicleRouterId));

    const clearSaving = () =>
      setSavingVehicleRouteIds((prev) => {
        const next = new Set(prev);
        next.delete(vehicleRouterId);
        return next;
      });

    const response = await createUetds(vehicleRouterId);

    // Hiç cevap yoksa (ör. yetki/özellik hatası) hook zaten toast gösterdi.
    if (!response) {
      clearSaving();
      return;
    }

    const { personelservice, uetds } = response;

    if (uetds?.success && personelservice?.uetdsSeferReferansNo) {
      store.UpdatePersonelService(personelservice);

      setCalculatedTransfers((prev) =>
        prev.map((transfer) => {
          if (isSyntheticRoutePayload(transfer.route) || !matchesRouteIdentifier(transfer.route, vehicleRouterId)) {
            return transfer;
          }
          return {
            ...transfer,
            route: { ...transfer.route, uetdsSeferReferansNo: personelservice.uetdsSeferReferansNo },
          };
        }),
      );

      clearSaving();
      return;
    }

    // Başarısız: satır kolonlarına "Hata" yazmak yerine üstteki toast ile bilgilendir.
    if (uetds?.noResponse) {
      window.toast.error(t("transferReport.uetdsNoResponse"));
    } else if (uetds?.message) {
      window.toast.error(uetds.message);
    } else {
      window.toast.error(t("transferReport.uetdsCreateFailed"));
    }

    clearSaving();
  };

  const driverComponent = (vehicleRoute) => {
    let currentDriverName = "";

    try {
      currentDriverName = vehicleRoute.drivers[0].name;
    } catch {
      return <p>{t("transferReport.driverNotFound")}</p>;
    }
    if (!currentDriverName) {
      return <p>{t("transferReport.driverNotFound")}</p>;
    }

    const disableInteractions = isSyntheticRoutePayload(vehicleRoute);
    const driverSelectElementId = `vehiclerouterid_driver_${vehicleRoute._id}`;
    const selectedDriverId =
      formStateDriver.inputs?.[driverSelectElementId]?.value ||
      vehicleRoute.drivers[0]?._id ||
      vehicleRoute.drivers[0]?.id;
    const storeDrivers = Array.isArray(store.drivers) ? store.drivers : [];
    const routeDrivers = Array.isArray(vehicleRoute?.drivers) ? vehicleRoute.drivers : [];
    const selectedDriverRecord =
      storeDrivers.find((driver) => driver.id?.toString() === selectedDriverId?.toString()) ||
      routeDrivers.find((driver) => driver.id?.toString() === selectedDriverId?.toString()) ||
      null;
    const derivedPlate = selectedDriverRecord?.plate || vehicleRoute.plate || "";

    const currentDriverId = vehicleRoute.drivers[0]?._id || vehicleRoute.drivers[0]?.id;
    const status = rowStatuses.get(vehicleRoute._id);

    return defaultFormat ? (
      <div style={{ position: "relative", paddingBottom: "18px" }}>
        <CustomSelect
          options={store.driverOptions}
          key={`vehiclerouterid_driver_${vehicleRoute._id}_${vehicleRoute.drivers[0]?._id}`}
          id={`vehiclerouterid_driver_${vehicleRoute._id}`}
          onInput={(id, value, isValid) => {
            inputHandlerDriver(id, value, isValid);
            if (!disableInteractions && value !== currentDriverId) {
              saveDriverHandler(vehicleRoute._id, value);
            }
          }}
          initialValue={store.driverOptions.find((option) => vehicleRoute.drivers[0]?._id === option.value)}
          initialValid={true}
          disabled={disableInteractions || savingVehicleRouteIds.has(vehicleRoute._id)}
        ></CustomSelect>
        <span
          style={{
            position: "absolute",
            bottom: "0",
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: status ? "11px" : "12px",
            fontWeight: status ? "normal" : "500",
            whiteSpace: "nowrap",
            color:
              status === "saving"
                ? "#1976d2"
                : status === "saved"
                  ? "#2e7d32"
                  : status === "error"
                    ? "#d32f2f"
                    : "#333",
          }}
        >
          {status === "saving" && `⏳ ${t("transferReport.saving")}`}
          {status === "saved" && `✓ ${t("transferReport.saved")}`}
          {status === "error" && `✗ ${t("transferReport.saveError")}`}
          {!status && derivedPlate}
        </span>
      </div>
    ) : (
      <div>
        <p>{currentDriverName}</p>
      </div>
    );
  };

  const activeChangeComponent = (vehicleRoute) => {
    let currentDriverName = "";

    try {
      currentDriverName = vehicleRoute.drivers[0].name;
    } catch {
      return <p>{t("transferReport.driverNotFound")}</p>;
    }
    if (!currentDriverName) {
      return <p>{t("transferReport.driverNotFound")}</p>;
    }

    const isCurrentlyActive = vehicleRoute?.isActive === false ? false : true;
    const disableInteractions = isSyntheticRoutePayload(vehicleRoute);
    const status = rowStatuses.get(vehicleRoute._id);

    return defaultFormat ? (
      <div style={{ position: "relative", paddingBottom: "18px" }}>
        <CustomSelect
          options={isActiveOptions}
          key={`vehiclerouterid_isActive_${vehicleRoute._id}_${vehicleRoute.isActive}`}
          id={`vehiclerouterid_isActive_${vehicleRoute._id}`}
          onInput={(id, value, isValid) => {
            inputHandlerDriver(id, value, isValid);
            if (!disableInteractions && value !== isCurrentlyActive) {
              saveIsActiveHandler(vehicleRoute._id, value);
            }
          }}
          initialValue={isCurrentlyActive ? isActiveOptions[1] : isActiveOptions[0]}
          initialValid={true}
          disabled={disableInteractions || savingVehicleRouteIds.has(vehicleRoute._id)}
        ></CustomSelect>
        {status && (
          <span
            style={{
              position: "absolute",
              bottom: "0",
              left: 0,
              right: 0,
              textAlign: "center",
              fontSize: "11px",
              whiteSpace: "nowrap",
              color: status === "saving" ? "#1976d2" : status === "saved" ? "#2e7d32" : "#d32f2f",
            }}
          >
            {status === "saving" && `⏳ ${t("transferReport.saving")}`}
            {status === "saved" && `✓ ${t("transferReport.saved")}`}
            {status === "error" && `✗ ${t("transferReport.saveError")}`}
          </span>
        )}
      </div>
    ) : (
      <div>
        <p>{currentDriverName}</p>
      </div>
    );
  };

  const completionStatusComponent = (vehicleRoute) => {
    const selectedStatusOption =
      serviceCompletionStatusOptions.find(
        (option) => option.value?.toString() === vehicleRoute?.completionStatus?.toString(),
      ) || serviceCompletionStatusOptions[0];
    const currentStatusLabel = selectedStatusOption?.label || "-";
    const disableInteractions = isSyntheticRoutePayload(vehicleRoute);
    const status = rowStatuses.get(vehicleRoute._id);

    if (!defaultFormat) {
      return (
        <div>
          <p>{currentStatusLabel}</p>
        </div>
      );
    }

    return (
      <div style={{ position: "relative", paddingBottom: "18px" }}>
        <CustomSelect
          options={serviceCompletionStatusOptions}
          key={`vehiclerouterid_completionStatus_${vehicleRoute._id}_${vehicleRoute?.completionStatus ?? "none"}`}
          id={`vehiclerouterid_completionStatus_${vehicleRoute._id}`}
          onInput={(id, value, isValid) => {
            inputHandlerDriver(id, value, isValid);
            const currentValue = vehicleRoute?.completionStatus ?? serviceCompletionStatusOptions[0]?.value;
            if (!disableInteractions && value !== currentValue) {
              saveCompletionStatusHandler(vehicleRoute._id, value);
            }
          }}
          initialValue={selectedStatusOption}
          initialValid={true}
          disabled={disableInteractions || savingVehicleRouteIds.has(vehicleRoute._id)}
        ></CustomSelect>
        {status && (
          <span
            style={{
              position: "absolute",
              bottom: "0",
              left: 0,
              right: 0,
              textAlign: "center",
              fontSize: "11px",
              whiteSpace: "nowrap",
              color: status === "saving" ? "#1976d2" : status === "saved" ? "#2e7d32" : "#d32f2f",
            }}
          >
            {status === "saving" && `⏳ ${t("transferReport.saving")}`}
            {status === "saved" && `✓ ${t("transferReport.saved")}`}
            {status === "error" && `✗ ${t("transferReport.saveError")}`}
          </span>
        )}
      </div>
    );
  };

  const downloadExcel = () => {
    const excelColumns = t("transferReport.excel.columns", { returnObjects: true });
    const pickupLabel = t("transferReport.transferTypePickup");
    const dropoffLabel = t("transferReport.transferTypeDropoff");
    const fileName = t("transferReport.excel.fileName");
    const sheetName = t("transferReport.excel.sheetName");

    if (!calculatedTransfers.length) {
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = [
      { header: excelColumns.school, key: "school", width: 30 },
      { header: excelColumns.transferDate, key: "transferDate", width: 15 },
      { header: excelColumns.transferName, key: "transferName", width: 40 },
      { header: excelColumns.transferType, key: "transferType", width: 15 },
      { header: excelColumns.transferTime, key: "transferTime", width: 15 },
      { header: excelColumns.passengers, key: "passengers", width: 50 },
      { header: excelColumns.driver, key: "driver", width: 50 },
      { header: excelColumns.completionStatus, key: "completionStatus", width: 25 },
    ];

    calculatedTransfers.forEach((transfer) => {
      worksheet.addRow({
        school: store.schoolOptions.find((school) => school.value === transfer.transferInfo.school)?.label || "-",
        transferDate: transfer.transferInfo.transferdate,
        transferName: transfer.transferInfo.name,
        transferType: transfer.route.type.toString() === "100" ? pickupLabel : dropoffLabel,
        transferTime: transfer.transferInfo.transfertime.split(" ")[4].substring(0, 5),
        passengers: transfer?.people.join("\n"),
        driver: transfer?.route?.drivers[0]?.name || "-",
        completionStatus: getCompletionStatusLabel(transfer.route?.completionStatus),
      });
    });

    worksheet.getColumn("passengers").alignment = { wrapText: true };

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = { vertical: "middle", wrapText: true };
      });
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    });
  };

  if ((isLoading || !today) && savingVehicleRouteIds.size === 0) {
    return (
      <div className="center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <React.Fragment>
      <form>
        <Grid
          sx={{
            padding: "16px",
            paddingBottom: "0px",
            fontsize: 16,
            flexGrow: 1,
            display: "flex",
            flexDirection: { xs: "row", md: "row" },
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "left",
            width: "100%",
            id: "report-receipts-container",
          }}
          container
          spacing={1}
        >
          <Grid item xs={12} md={4}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <p style={{ margin: "0", fontWeight: 500 }}>{t("transferReport.institutionLabel")}</p>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="checkbox"
                      style={{ width: "18px", height: "18px" }}
                      checked={areAllSchoolsSelected}
                      onChange={(event) => handleSelectAllSchools(event.target.checked)}
                    />
                    {t("transferReport.allInstitutions")}
                  </label>
                </Grid>
                {filteredSchoolOptions.map((school) => {
                  const schoolId = school.value.toString();
                  const isChecked = selectedSchools.includes(schoolId);
                  return (
                    <Grid item xs={12} sm={6} key={`transfer-report-school-${schoolId}`}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input
                          type="checkbox"
                          style={{ width: "18px", height: "18px" }}
                          checked={isChecked}
                          onChange={() => handleSchoolToggle(schoolId)}
                        />
                        {school.label}
                      </label>
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          </Grid>

          <Grid item xs={12} md={1}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <p style={{ margin: "0", fontWeight: 500 }}>{t("transferReport.serviceTypeLabel")}</p>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={selectedServiceTypes.personel}
                  onChange={(e) => setSelectedServiceTypes((prev) => ({ ...prev, personel: e.target.checked }))}
                />
                {t("transferReport.serviceTypePersonel")}
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={selectedServiceTypes.shift}
                  onChange={(e) => setSelectedServiceTypes((prev) => ({ ...prev, shift: e.target.checked }))}
                />
                {t("transferReport.serviceTypeShift")}
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={selectedServiceTypes.transfer}
                  onChange={(e) => setSelectedServiceTypes((prev) => ({ ...prev, transfer: e.target.checked }))}
                />
                {t("transferReport.serviceTypeTransfer")}
              </label>
            </div>
          </Grid>

          <Grid item xs={1}>
            <DatePicker
              id="datestarter"
              onInput={inputHandler}
              label={t("transferReport.startDateLabel")}
              errorText={t("transferReport.startDateError")}
              initialValid={true}
              initialValue={initDateSelected}
              validators={[VALIDATOR_REQUIRE()]}
            ></DatePicker>
          </Grid>
          <Grid item xs={1}>
            <DatePicker
              id="datefinisher"
              initialValid={true}
              onInput={inputHandler}
              label={t("transferReport.endDateLabel")}
              errorText={t("transferReport.endDateError")}
              initialValue={finishDateSelected}
              validators={[VALIDATOR_REQUIRE()]}
            ></DatePicker>
          </Grid>
          <Grid item xs={1}>
            <div style={{ position: "relative", top: "8px" }}>
              <Button type="button" disabled={!formState.isValid || !selectedSchools.length} onClick={createReport}>
                {t("transferReport.generateReport")}
              </Button>
            </div>
          </Grid>
          <Grid item xs={1}>
            <div style={{ position: "relative", top: "8px" }}>
              <Button type="button" onClick={downloadExcel} disabled={!calculatedTransfers.length}>
                {t("transferReport.downloadReport")}
              </Button>
            </div>
          </Grid>
        </Grid>
        <div style={{ paddingLeft: "20px", marginBottom: "20px", display: "flex" }}>
          <CheckBox
            id="showAll"
            label={t("transferReport.showInactiveTransfers")}
            initialValue={false}
            initialValid={true}
            onInput={inputHandler}
            validators={[VALIDATOR_NONE()]}
          />
        </div>
      </form>

      <div key={itemState} id="printableArea" style={{ display: "flex", flex: "1", flexDirection: "row" }}>
        <div style={{ width: "100%", marginBottom: "20px" }}>
          {/* <h4 style={{ paddingLeft: "16px", marginTop: "0px" }}>
            <span>Kırmızı Havalanına Gidiyor</span> | <span>Mavi Havalanından Dönüyor</span> | Kurum | Tarih | İsim |
            Havaalanı Saati | Yolcular | Sürücü | Transfer | Durum | Uetds Sefer Referans No
          </h4> */}

          <table style={{ borderCollapse: `${defaultFormat ? "inherit" : "collapse"}` }}>
            <tbody>
              <tr>
                <th style={{ width: "5px" }}></th>
                <th style={{ width: "50px" }}></th>
                <th style={{ width: "150px" }}></th>
                <th style={{ width: "100px" }}></th>
                <th style={{ width: "150px" }}></th>
                <th style={{ width: "70px" }}></th>
                <th style={{ width: "250px" }}></th>
                <th style={{ width: "200px" }}></th>
                <th style={{ width: "100px" }}></th>
                <th style={{ width: "200px" }}></th>
                <th style={{ width: "180px" }}></th>
                <th style={{ width: "180px" }}></th>
                <th style={{ width: "180px" }}></th>
              </tr>

              {calculatedTransfers.map((transfer) => {
                const routeTypeValue = transfer.transferInfo?.type?.toString() || LEGACY_PICKUP_TYPE;
                const rowKey =
                  transfer.uniqueKey ||
                  `${transfer.transferInfo?._id || transfer.transferInfo?.id || Math.random().toString()}-${
                    transfer.route?._id || transfer.route?.id || ""
                  }`;
                const isSynthetic = isSyntheticTransferRow(transfer);
                const isSyntheticWeekday = Boolean(
                  transfer.isSyntheticPersonelService || transfer.route?.isSyntheticPersonelService,
                );
                const isSyntheticShift = Boolean(
                  transfer.isSyntheticShiftService || transfer.route?.isSyntheticShiftService,
                );
                const syntheticLabel = isSyntheticWeekday
                  ? "Standart Servis"
                  : isSyntheticShift
                    ? "Vardiya Servisi"
                    : "";

                return (
                  <tr
                    key={rowKey}
                    style={{
                      backgroundColor: defaultFormat ? "#dddddd" : "#dddddd",
                      textAlign: "center",
                      position: "relative",
                      left: `${routeTypeValue === LEGACY_PICKUP_TYPE || !defaultFormat ? "0" : "384px"}`,
                      borderBottom: `${defaultFormat ? "none" : "1px solid black"}`,
                      height: "160px",
                      display: `${
                        formState.inputs.showAll.value ||
                        transfer.route.isActive !== false ||
                        recentlyPassiveIds.has(transfer.route?._id)
                          ? "table-row"
                          : "none"
                      }`,
                    }}
                    className={savingVehicleRouteIds.has(transfer.route?._id) ? "glow-update" : ""}
                  >
                    <td style={{ position: "relative", padding: 0, width: "5px" }}>
                      <div
                        style={{
                          backgroundColor: routeTypeValue === LEGACY_PICKUP_TYPE ? "#830000" : "#4460aa",
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          width: "100%",
                        }}
                      ></div>
                    </td>
                    <td style={{ padding: "10px", justifyContent: "center" }}>
                      {!isSynthetic && (
                        <IconButton
                          aria-label="open-transfer"
                          size="small"
                          title={t("transferReport.openTransfer")}
                          onClick={() => {
                            setSelectedTransferId(transfer.transferInfo?._id || transfer.transferInfo?.id);
                            setShowServiceModal(true);
                          }}
                        >
                          <OpenInNewIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </td>
                    <td>
                      {store.schoolOptions.find((school) => school.value === transfer.transferInfo.school)?.label}
                    </td>

                    <td>{transfer.transferInfo.transferdate}</td>
                    <td>{transfer.transferInfo.name}</td>
                    <td>{transfer.transferInfo.transfertime.split(" ")[4].substring(0, 5)}</td>
                    <td style={{ verticalAlign: "middle" }}>{peopleComponent(transfer.people)}</td>
                    <td style={{ verticalAlign: "middle" }}>{transfer.route.name}</td>
                    <td style={{ verticalAlign: "middle" }}>
                      {t(TRAVEL_STATUS_TYPE_EXPLANATIONS.find((exp) => exp.value === transfer.route.status)?.label || "-")}
                    </td>
                    <td style={{ verticalAlign: "middle", padding: "8px 4px" }} key={`${rowKey}-driver`}>
                      {driverComponent(transfer.route)}
                    </td>
                    <td style={{ verticalAlign: "middle", padding: "8px 4px" }}>
                      {activeChangeComponent(transfer.route)}
                    </td>
                    <td style={{ verticalAlign: "middle", padding: "8px 4px" }}>
                      {completionStatusComponent(transfer.route)}
                    </td>
                    <td key={`${rowKey}-uetds`}>
                      {isSynthetic ? (
                        syntheticLabel
                      ) : isInvalidPlate(transfer.route?.plate) ? (
                        t("transferReport.invalidPlate")
                      ) : getPlateMiddleLetters(transfer.route?.plate) === "C" ? (
                        t("transferReport.cPlate")
                      ) : (
                        <>
                          UETDS <br></br>
                          {transfer?.route?.uetdsSeferReferansNo || t("transferReport.noUetdsRecord")}
                          {shouldShowCreateUetds(transfer.route) && (
                            <div style={{ marginTop: "6px" }}>
                              <Button
                                size="small"
                                type="button"
                                disabled={savingVehicleRouteIds.has(transfer.route?._id)}
                                onClick={() => createUetdsHandler(transfer.route._id)}
                              >
                                {t("transferReport.createUetds")}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showServiceModal && (
        <Modal
          show={showServiceModal}
          style={{ width: "80vw", height: "80vh", marginLeft: "-40vw", top: "11vh" }}
          headerStyle={{ padding: "0px", paddingTop: "20px" }}
          onCancel={handleServiceModalClose}
          // onClose={handleServiceModalClose}
          // header={"Servis Detayı"}
          footerClass="delete-modal-footer"
          // footer={
          //   <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          //     <Button danger onClick={() => setShowServiceModal(false)}>
          //       Kapat
          //     </Button>
          //   </div>
          // }
        >
          {selectedTransferId && (
            <ManagedTransfer itemId={selectedTransferId} onClose={() => {}} showTransferInfo={false} />
          )}
        </Modal>
      )}
    </React.Fragment>
  );
};

export default TransferReport;
